import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import type {
  TransferApplication,
  UpdateTransferApplicationParams,
  UpdateTransferApplicationResponse,
} from "../types"

/**
 * useUpdateTransferApplicationフックの戻り値の型定義
 */
interface UseUpdateTransferApplicationReturn {
  /** 立替送金申請を更新する関数 */
  updateTransferApplication: (
    id: number,
    params: UpdateTransferApplicationParams
  ) => Promise<TransferApplication>
  /** 更新処理中かどうか */
  isUpdating: boolean
  /** 更新処理でのエラー */
  error: Error | null
  /** エラーをリセットする関数 */
  resetError: () => void
}

/**
 * 立替送金申請を更新するためのカスタムフック
 *
 * @returns 更新機能とその状態
 *
 * @example
 * ```typescript
 * const { updateTransferApplication, isUpdating, error } = useUpdateTransferApplication()
 *
 * const handleUpdate = async () => {
 *   try {
 *     const updated = await updateTransferApplication(123, {
 *       transferOn: '2024-01-01',
 *       applyNo: 'APPLY123'
 *     })
 *     console.log('更新完了:', updated)
 *   } catch (err) {
 *     console.error('更新エラー:', err)
 *   }
 * }
 * ```
 */
export const useUpdateTransferApplication = (): UseUpdateTransferApplicationReturn => {
  const { putWithToken } = useSmoothApi()
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * エラーをリセットする関数
   */
  const resetError = () => {
    setError(null)
  }

  /**
   * 立替送金申請を更新する
   * @param id - 申請ID
   * @param params - 更新パラメータ
   * @returns 更新された申請データ
   */
  const updateTransferApplication = async (
    id: number,
    params: UpdateTransferApplicationParams
  ): Promise<TransferApplication> => {
    setIsUpdating(true)
    setError(null)

    try {
      const url = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/transfer_applications/${id}`
      const response = await putWithToken(url, params)
      const data = response.data as UpdateTransferApplicationResponse

      return data.transferApplication
    } catch (err) {
      const error = err instanceof Error ? err : new Error("立替送金申請の更新に失敗しました")
      console.error("立替送金申請の更新に失敗しました:", err)
      setError(error)
      throw error
    } finally {
      setIsUpdating(false)
    }
  }

  return {
    updateTransferApplication,
    isUpdating,
    error,
    resetError,
  }
}
